import React, {useState, useEffect, useRef, useContext, useMemo} from 'react';
import {Carousel} from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import {CSSTransition} from "react-transition-group";
import {useLocation, useNavigate} from "react-router-dom";

import styles from './ShopOffers.module.scss';
import CI_SPECIAL_OFFERS from '../../assets/images/category_icons/ci_special_offers.png';
import CI_DAILY_OFFERS from '../../assets/images/category_icons/ci_daily_offers.png';
import CI_GEMS from '../../assets/images/category_icons/ci_gems.png';
import CI_SPECIAL_EVENTS from '../../assets/images/category_icons/ci_special_events.png';
import CI_CRATES from '../../assets/images/category_icons/ci_crates.png';
import CI_RESOURCES from '../../assets/images/category_icons/ci_resources.png';
import CI_EXCLUSIVE_OFFERS from '../../assets/images/category_icons/ci_exclusive_offers.png';
import CI_COMMANDER_OFFERS from '../../assets/images/category_icons/ci_commander.png';
// import CI_EVENT_PASS from '../../assets/images/category_icons/ci_event_pass.png';
import WarToken from '../../assets/images/war_token.png';
import SpecialOffer from "../../components/SpecialOffer/SpecialOffer";
import GemsPackage from "../../components/GemsPackage/GemsPackage";
import CratesPackage from "../../components/CratesPackage/CratesPackage";
import ResourcesPackage from "../../components/ResourcesPackage/ResourcesPackage";
import FreeOffer from "../../components/FreeOffer/FreeOffer";
import {OFFER_NAME_TO_CONFIG_TYPE, OFFER_TYPE_TO_CONFIG_ICON, OFFER_TYPE_TO_CONFIG_NAME, ENV} from "../../shared/constants";
import FilterMobile from "../../components/Filter/FilterMobile";
import { MilestonesContext } from '../../App';
import CommandersPackages from "../../components/CommandersPackages/CommanderPackages";
import FortuneEventTokens from '../../components/FortuneEventTokens/FortuneEventTokens';
import Flares from '../../components/Flares/Flares';
import Gambit from '../../components/Gambing/Gambit';
import {GA} from "../../shared/helpers";
import PromoNotification from "../../components/PromoNotification/PromoNotification";
import {hasAnyPurchaseBonus, PURCHASE_BONUS_BANNER_ICON, PURCHASE_BONUS_BANNER_TEXT_KEY} from "../../shared/purchaseBonusConfig";

/** Base URL for event pass images stored in Azure Blob Storage */
const CI_EVENT_PASS_BASE_URL = 'https://b17.blob.core.windows.net/b17-offerset/global/';

/**
 * Renders offers in a carousel format for mobile view
 * @param {string} title - The category title to display
 * @param {Array} offers - Array of offer objects to render
 * @param {Function} selectOfferById - Callback function to handle offer selection
 * @param {string} currency - Currency symbol/code for pricing
 * @param {boolean} isEveryDayOffer - Flag indicating if these are daily offers
 * @param {string} endsStr - Text string for offer end time display
 * @param {string} categoryImg - Category icon image source
 * @param {string} categoryId - Unique identifier for the category
 * @param {boolean} isEventPassOffer - Flag indicating if these are event pass offers
 * @returns {JSX.Element} Carousel component with offers
 */
const renderOffersInCarousel = (
    title,
    offers,
    selectOfferById,
    currency,
    isEveryDayOffer,
    endsStr,
    categoryImg,
    categoryId,
    isEventPassOffer
) => {
    return (
        <div id={categoryId} className={styles.specialOffersWrapper}>
            <div className={styles.specialOffersTitle}>
                <img className={styles.categoryIcon} src={categoryImg} alt={''} />
                {title}
            </div>
            <Carousel
                autoPlay={true}
                infiniteLoop={true}
                interval={5000}
                showArrows={false}
                showStatus={false}
                stopOnHover={true}
                showThumbs={false}
                showIndicators={offers.length > 1}
                preventMovementUntilSwipeScrollTolerance={true}
                swipeScrollTolerance={50}
            >
                {offers.map((offer, index) => (
                    <SpecialOffer
                        {...offer}
                        ImageURL={isEventPassOffer ?
                            `${CI_EVENT_PASS_BASE_URL}${ENV === 'live' ? 'production' : 'dev'}/images/event_pass_img_${offer?.EventVersion}_${offer.OfferId}.png`
                        :
                            offer.ImageURL
                        }
                        key={index}
                        selectOfferById={selectOfferById}
                        currency={currency}
                        isEveryDayOffer={isEveryDayOffer}
                        endsStr={endsStr}
                    />
                ))}
            </Carousel>
        </div>
    );
};

/**
 * Renders offers in a normal grid layout for desktop view
 * @param {string} title - The category title to display
 * @param {Array} offers - Array of offer objects to render
 * @param {Function} selectOfferById - Callback function to handle offer selection
 * @param {string} currency - Currency symbol/code for pricing
 * @param {boolean} isEveryDayOffer - Flag indicating if these are daily offers
 * @param {string} endsStr - Text string for offer end time display
 * @param {string} categoryImg - Category icon image source
 * @param {string} categoryId - Unique identifier for the category
 * @param {boolean} isEventPassOffer - Flag indicating if these are event pass offers
 * @returns {JSX.Element} Grid layout component with offers
 */
const renderOffersNormal = (
    title,
    offers,
    selectOfferById,
    currency,
    isEveryDayOffer,
    endsStr,
    categoryImg,
    categoryId,
    isEventPassOffer
) => {
    return (
        <div id={categoryId} className={styles.specialOffersWrapper}>
            <CSSTransition in appear timeout={300} classNames={'fade-in-slide-down'}>
                <div className={`${styles.specialOffersTitle} ${isEveryDayOffer ? styles.everyDayOffer : ''}`}>
                    <img className={styles.categoryIcon} src={categoryImg} alt={''} />
                    {title}
                </div>
            </CSSTransition>
            <div className={styles.offersWrapper}>
                {offers.map((offer, index) => (
                    <SpecialOffer
                        {...offer}
                        ImageURL={isEventPassOffer ?
                            `${CI_EVENT_PASS_BASE_URL}${ENV === 'live' ? 'production' : 'dev'}/images/event_pass_img_${offer?.EventVersion}_${offer.OfferId}.png`
                        :
                            offer.ImageURL
                        }
                        selectOfferById={selectOfferById}
                        currency={currency}
                        key={index}
                        isEveryDayOffer={isEveryDayOffer}
                        endsStr={endsStr}
                    />
                ))}
            </div>
        </div>
    );
};

/**
 * Renders Gambit offers in a carousel format for mobile view
 * @param {string} title - The category title to display
 * @param {Array} offers - Array of Gambit offer objects to render
 * @param {Function} selectOfferById - Callback function to handle offer selection
 * @param {string} currency - Currency symbol/code for pricing
 * @param {Object} config - Configuration object containing localized strings
 * @param {string} categoryImg - Category icon image source
 * @param {string} categoryId - Unique identifier for the category
 * @returns {JSX.Element} Carousel component with Gambit offers
 */
const renderGambitOffersInCarousel = (
    title,
    offers,
    selectOfferById,
    currency,
    config,
    categoryImg,
    categoryId,
) => {
    return (
        <Carousel
            autoPlay={true}
            infiniteLoop={true}
            interval={5000}
            showArrows={false}
            showStatus={false}
            stopOnHover={true}
            showThumbs={false}
            showIndicators={offers.length > 1}
            preventMovementUntilSwipeScrollTolerance={true}
            swipeScrollTolerance={50}
        >
            {offers.map((offer, index) => (
                <Gambit
                {...offer}
                key={index}
                selectOfferById={selectOfferById}
                currency={currency}
                config={config}
                isReviewOrder={false}
                />
            ))}
        </Carousel>
    );
};

/**
 * Renders Gambit offers in a normal grid layout for desktop view
 * @param {string} title - The category title to display
 * @param {Array} offers - Array of Gambit offer objects to render
 * @param {Function} selectOfferById - Callback function to handle offer selection
 * @param {string} currency - Currency symbol/code for pricing
 * @param {Object} config - Configuration object containing localized strings
 * @param {string} categoryImg - Category icon image source
 * @param {string} categoryId - Unique identifier for the category
 * @returns {JSX.Element} Grid layout component with Gambit offers
 */
const renderGambitOffersNormal = (
    title,
    offers,
    selectOfferById,
    currency,
    config,
    categoryImg,
    categoryId,
) => {
    return (
        offers.map((offer, index) => (
            <Gambit
                {...offer}
                key={index}
                selectOfferById={selectOfferById}
                currency={currency}
                config={config}
                isReviewOrder={false}
            />
        ))
    )
};

/**
 * Renders free offers in a carousel format for mobile view
 * @param {Array} freeOffers - Array of free offer objects to render
 * @param {Function} claimFreeOffer - Callback function to handle free offer claims
 * @param {string} buttonName - Text for the claim button
 * @param {string} title - The category title to display
 * @returns {JSX.Element} Carousel component with free offers
 */
const renderFreeOffersInCarousel = (freeOffers, claimFreeOffer, buttonName, title) => {
    return (
        <div className={styles.specialOffersWrapper}>
            <div className={styles.specialOffersTitle}>{title}</div>
            <Carousel
                autoPlay={true}
                infiniteLoop={true}
                interval={5000}
                showArrows={false}
                showStatus={false}
                stopOnHover={true}
                showThumbs={false}
                showIndicators={freeOffers.length > 1}
            >
                {freeOffers.map((offer, index) => (
                    <FreeOffer {...offer} claimFreeOffer={claimFreeOffer} buttonName={buttonName} key={index} />
                ))}
            </Carousel>
        </div>
    );
};

/**
 * Renders free offers in a normal grid layout for desktop view
 * @param {Array} freeOffers - Array of free offer objects to render
 * @param {Function} claimFreeOffer - Callback function to handle free offer claims
 * @param {string} buttonName - Text for the claim button
 * @param {string} title - The category title to display
 * @returns {JSX.Element} Grid layout component with free offers
 */
const renderFreeOffers = (freeOffers, claimFreeOffer, buttonName, title) => {
    return (
        <div className={styles.specialOffersWrapper}>
            <CSSTransition in appear timeout={300} classNames={'fade-in-slide-down'}>
                <div className={styles.specialOffersTitle}>{title}</div>
            </CSSTransition>
            <div className={styles.offersWrapper}>
                {freeOffers.map((offer, index) => (
                    <FreeOffer {...offer} claimFreeOffer={claimFreeOffer} buttonName={buttonName} key={index} />
                ))}
            </div>
        </div>
    );
};

/**
 * ShopOffers Component
 * Main component for displaying all shop offers organized by categories.
 * Handles filtering, category navigation, scroll-based active category tracking,
 * and responsive rendering for mobile and desktop views.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.eventPassOffers - Event pass offers array
 * @param {Array} props.specialOffers - Special offers array
 * @param {Array} props.gems - Gem packages array
 * @param {Array} props.everyDayOffers - Daily offers array
 * @param {Array} props.crates - Crate packages array
 * @param {Object} props.resources - Resources packages grouped by type
 * @param {Function} props.selectOfferById - Callback to handle offer selection
 * @param {Object} props.config - Configuration object with localized strings
 * @param {Object} props.windowSize - Object containing window size information
 * @param {string} props.currency - Currency symbol/code for pricing
 * @param {Object} props.offersCategories - Object defining visible categories
 * @param {Function} props.filterCategory - Callback to filter categories
 * @param {Function} props.openCrateInfoPopUp - Callback to open crate info popup
 * @param {Array} props.gambitLeaguePackages - Gambit league packages array
 * @param {Array} props.fortuneEventTokens - Fortune event tokens array
 * @param {Array} props.freeOffers - Free offers array
 * @param {Function} props.claimFreeOffer - Callback to claim free offers
 * @param {Array} props.webShopExclusiveOffers - Web shop exclusive offers array
 * @param {Object} props.commanderOffers - Commander offers grouped by type
 * @returns {JSX.Element} ShopOffers component
 */
const ShopOffers = ({
    eventPassOffers,
    specialOffers,
    gems,
    everyDayOffers,
    crates,
    resources,
    selectOfferById,
    config,
    windowSize,
    currency,
    offersCategories,
    filterCategory,
    openCrateInfoPopUp,
    gambitLeaguePackages,
    fortuneEventTokens,
    flares,
    freeOffers,
    claimFreeOffer,
    webShopExclusiveOffers,
    commanderOffers
}) => {
    const navigate = useNavigate();
    const { hash } = useLocation();
    let isFirstPageView = useRef(true);
    const [activeCategory, setActiveCategory] = useState(OFFER_NAME_TO_CONFIG_TYPE.specialOffers);
    const [eventPassIcon, setEventPassIcon] = useState('');
    const [categoriesOffers, setCategoriesOffers] = useState([]);
    const [isPromoNotificationClosed, setIsPromoNotificationClosed] = useState(false);
    const milestonesContext = useContext(MilestonesContext);
    const hasPurchaseBonus = hasAnyPurchaseBonus([crates, commanderOffers.Crates]);
    const selectedCategories = Object.values(offersCategories).filter(category => category.isVisible);
    
    // Flag to prevent scroll listener from updating active category during programmatic scrolling
    const isScrollingProgrammatically = useRef(false);
    // Timeout reference for debouncing scroll events
    const scrollTimeout = useRef(null);

    // Memoized Special Events data - calculates which subcategories are active and the dynamic title
    const specialEventsData = useMemo(() => {
        // Check both data availability AND visibility from OFFERS_CONFIG
        const hasFlares = flares?.length > 0 && flares?.isVisible !== false;
        const hasFortune = fortuneEventTokens?.length > 0 && fortuneEventTokens?.isVisible !== false;
        const hasGambit = gambitLeaguePackages?.length > 0 && gambitLeaguePackages?.isVisible !== false;
        const activeCount = [hasFlares, hasFortune, hasGambit].filter(Boolean).length;
        const hasAny = activeCount > 0;

        // Build subcategories array with their Category values for sorting
        const subcategories = [];
        if (hasFlares) {
            subcategories.push({
                type: 'flares',
                category: flares[0]?.Category ?? 999,
                data: flares,
                title: config.flaresTitle
            });
        }
        if (hasFortune) {
            subcategories.push({
                type: 'fortune',
                category: fortuneEventTokens[0]?.Category ?? 999,
                data: fortuneEventTokens,
                title: config.fortuneEventTokensTitle
            });
        }
        if (hasGambit) {
            subcategories.push({
                type: 'gambit',
                category: gambitLeaguePackages[0]?.Category ?? 999,
                data: gambitLeaguePackages,
                title: config.gambitLeaguePackages
            });
        }

        // Sort subcategories by Category value (ascending)
        const sortedSubcategories = subcategories.sort((a, b) => a.category - b.category);

        // Determine dynamic title based on active subcategories count
        let title = config.specialEvents;
        if (activeCount === 1 && sortedSubcategories.length > 0) {
            title = sortedSubcategories[0].title;
        }

        return { hasFlares, hasFortune, hasGambit, activeCount, hasAny, title, sortedSubcategories };
    }, [flares, fortuneEventTokens, gambitLeaguePackages, config]);

    /**
     * Navigates to a specific category by scrolling to its element
     * @param {string} categoryId - The ID of the category element to scroll to
     * @param {string} categoryName - The name of the category for URL hash
     */
    const goToCategory = (categoryId, categoryName) => {
        const element = document.getElementById(categoryId);
        if (element) {
            isScrollingProgrammatically.current = true;
            element.scrollIntoView({behavior: "smooth"});
            setActiveCategory(categoryId);
            navigate(`#${categoryName}`);
            // Reset the flag after scroll animation completes
            setTimeout(() => {
                isScrollingProgrammatically.current = false;
            }, 1000);
        } else {
            // Element might not be mounted yet due to CSSTransition
            // Try again after a short delay to allow the transition to complete
            setTimeout(() => {
                const delayedElement = document.getElementById(categoryId);
                if (delayedElement) {
                    isScrollingProgrammatically.current = true;
                    delayedElement.scrollIntoView({behavior: "smooth"});
                    setActiveCategory(categoryId);
                    navigate(`#${categoryName}`);
                    // Reset the flag after scroll animation completes
                    setTimeout(() => {
                        isScrollingProgrammatically.current = false;
                    }, 1000);
                }
            }, 450); // Slightly longer than the 400ms transition timeout
        }
    }

    // Handle initial category selection based on URL hash or available categories
    useEffect(() => {
        let activeCategoryName = '';
        if (hash) {
            // Use hash from URL if present
            activeCategoryName = hash;
            activeCategoryName = activeCategoryName.slice(1);
            activeCategoryName = OFFER_NAME_TO_CONFIG_TYPE[activeCategoryName];
            document.getElementById(activeCategoryName)?.scrollIntoView({behavior: "smooth"});
        } else {
            // Default to first available category in priority order
            if (offersCategories.specialEvents) {
                activeCategoryName = OFFER_NAME_TO_CONFIG_TYPE['specialEvents'];
            }
            else if (offersCategories.webShopExclusiveOffers) {
                activeCategoryName = OFFER_NAME_TO_CONFIG_TYPE['webShopExclusiveOffers'];
            } else if (offersCategories.eventPassOffers) {
                activeCategoryName = OFFER_NAME_TO_CONFIG_TYPE['eventPassOffers'];
            } else {
                activeCategoryName = OFFER_NAME_TO_CONFIG_TYPE['specialOffers'];
            }
        }
        setActiveCategory(activeCategoryName);
    }, [hash]);

    // Load event pass icon dynamically based on event version
    useEffect(() => {
        if (eventPassOffers && eventPassOffers.length > 0) {
            const eventPassOffer = eventPassOffers[0];
            setEventPassIcon(`${CI_EVENT_PASS_BASE_URL}${ENV === 'live' ? 'production' : 'dev'}/images/event_pass_logo_${eventPassOffer.EventVersion}.png`);
        }
    }, [eventPassOffers]);

    // Track page view for analytics on first render
    useEffect(() => {
        if (isFirstPageView.current && !GA.hasUserSawTheOffers) {
            if (ENV === 'live') {
                if (typeof window !== 'undefined' && window.gtag) {
                    GA.trackPageView('/shop', 'all offers');
                    GA.trackViewOfferList();
                }
            }
            isFirstPageView.current = false;
            GA.hasUserSawTheOffers = true;
        }
    }, []);
    
    // Rearrange categories to prioritize new fortune event and gambit league packages
    useEffect(() => {
        const offersCategoriesArray = Object.values(offersCategories);
    
        // Move items flagged isNew (e.g. Special Events) to the start
        const rearrangedOffers = [
            ...offersCategoriesArray.filter(item => item.isNew),
            ...offersCategoriesArray.filter(item => !item.isNew)
        ];
    
        setCategoriesOffers(rearrangedOffers);
    }, [offersCategories]);

    // Scroll-based active category tracking
    useEffect(() => {
        const handleScroll = () => {

            // Don't update active category if user clicked on a category (programmatic scroll)
            if (isScrollingProgrammatically.current) {
                return;
            }

            // Clear existing timeout
            if (scrollTimeout.current) {
                clearTimeout(scrollTimeout.current);
            }

            // Debounce scroll event
            scrollTimeout.current = setTimeout(() => {
                const visibleCategories = categoriesOffers
                    .filter(category => category.isVisible)
                    .map(category => category.type);

                if (visibleCategories.length === 0) return;

                const scrollPosition = window.scrollY;
                const windowHeight = window.innerHeight;
                const documentHeight = document.documentElement.scrollHeight;
                
                // Check if at the bottom of the page
                if (scrollPosition + windowHeight >= documentHeight - 50) {
                    const lastCategory = visibleCategories[visibleCategories.length - 1];
                    if (activeCategory !== lastCategory) {
                        setActiveCategory(lastCategory);
                        window.history.replaceState(null, '', `#${OFFER_TYPE_TO_CONFIG_NAME[lastCategory]}`);
                    }
                    return;
                }

                // Check if at the top of the page
                if (scrollPosition < 100) {
                    const firstCategory = visibleCategories[0];
                    if (activeCategory !== firstCategory) {
                        setActiveCategory(firstCategory);
                        window.history.replaceState(null, '', `#${OFFER_TYPE_TO_CONFIG_NAME[firstCategory]}`);
                    }
                    return;
                }

                // Find which category is currently in view (title at middle or above viewport)
                let currentCategory = null;
                const viewportMiddle = scrollPosition + (windowHeight / 2);

                for (const categoryType of visibleCategories) {
                    const element = document.getElementById(categoryType);
                    if (element) {
                        const rect = element.getBoundingClientRect();
                        const elementTop = rect.top + scrollPosition;
                        
                        // If category title is at the middle or above viewport middle
                        if (elementTop <= viewportMiddle) {
                            currentCategory = categoryType;
                        } else {
                            break;
                        }
                    }
                }

                // Update active category and URL hash if changed
                if (currentCategory && currentCategory !== activeCategory) {
                    setActiveCategory(currentCategory);
                    window.history.replaceState(null, '', `#${OFFER_TYPE_TO_CONFIG_NAME[currentCategory]}`);
                }
            }, 100); // 100ms debounce
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (scrollTimeout.current) {
                clearTimeout(scrollTimeout.current);
            }
        };
    }, [categoriesOffers, activeCategory]);

    return (
        <div className={styles.allOffersWrapper}>
            {/* Desktop category filter sidebar */}
            {!windowSize.isMobileView && Object.keys(offersCategories).length ?
                <div className={styles.filterWrapper}>
                    <div className={styles.filterTitle}>
                        <div className={styles.text}>{config.allCategories} {`(${selectedCategories.length})`}</div>
                    </div>
                    {categoriesOffers.map((category, index) => {
                        const categoryName = OFFER_TYPE_TO_CONFIG_NAME[category.type];
                        let categoryIcon = OFFER_TYPE_TO_CONFIG_ICON[category.type];
                        // Use dynamic event pass icon if available
                        if (category.type === OFFER_NAME_TO_CONFIG_TYPE.eventPassOffers) {
                            categoryIcon = eventPassIcon;
                        }

                        // Use dynamic title for specialEvents category
                        const displayName = category.type === OFFER_NAME_TO_CONFIG_TYPE.specialEvents
                            ? specialEventsData.title
                            : config[categoryName];

                        return (
                        <div
                            key={index}
                            className={`${styles.categoryWrapper} ${(category.type === activeCategory) ? styles.active : ''}`}
                            onClick={() => goToCategory(category.type, OFFER_TYPE_TO_CONFIG_NAME[category.type])}
                        >
                            <img className={styles.categoryFilterIcon} src={categoryIcon} alt={''} />
                            <div className={styles.categoryName}>{displayName}</div>
                            {category.isNew && <div className={styles.badgeNew}>new</div>}
                        </div>)
                    })}
                </div>
                :
                null
            }
            <div className={styles.offersSideWrapper}>
            {/* War tokens milestone description banner */}
            <CSSTransition
                in={hasPurchaseBonus && !isPromoNotificationClosed}
                timeout={400}
                classNames="fade"
                unmountOnExit
            >
                <PromoNotification
                    icon={PURCHASE_BONUS_BANNER_ICON}
                    text={config[PURCHASE_BONUS_BANNER_TEXT_KEY]}
                    onClose={() => setIsPromoNotificationClosed(true)}
                    isHighlighted
                />
            </CSSTransition>
            <CSSTransition
                in={!milestonesContext.isMilestoneDescriptionClosed}
                timeout={400}
                classNames="fade"
                unmountOnExit
            >
                <PromoNotification
                    icon={WarToken}
                    text={<>{config.getWarTokens}&nbsp;{config.warTokensDesc}</>}
                    onClose={() => milestonesContext.setIsMilestoneDescriptionClosed(true)}
                />
            </CSSTransition>
                {/* FREE OFFERS SECTION - Currently disabled due to exploit */}
                {/*<CSSTransition*/}
                {/*    in={freeOffers && freeOffers.length > 0}*/}
                {/*    timeout={400}*/}
                {/*    classNames="fade"*/}
                {/*    unmountOnExit*/}
                {/*>*/}
                {/*    {windowSize.isMobileView ?*/}
                {/*        renderFreeOffersInCarousel(freeOffers, claimFreeOffer, config.free, config.freeOffers)*/}
                {/*        :*/}
                {/*        renderFreeOffers(freeOffers, claimFreeOffer, config.free, config.freeOffers)*/}
                {/*    }*/}
                {/*</CSSTransition>*/}
                {/* Special Events Section - Combined category for Flares, Fortune Tokens and Gambit League */}
                <CSSTransition
                    in={specialEventsData.hasAny && offersCategories.specialEvents?.isVisible}
                    timeout={400}
                    classNames="fade"
                    unmountOnExit
                >
                    <div id={OFFER_NAME_TO_CONFIG_TYPE.specialEvents} className={styles.gemsWrapper}>
                        <div className={styles.gemsTitle}>
                            <img className={styles.categoryIcon} src={CI_SPECIAL_EVENTS} alt={''} />
                            {specialEventsData.title}
                        </div>
                        {/* Render subcategories in sorted order based on Category parameter */}
                        {specialEventsData.sortedSubcategories.map((subcategory, index) => {
                            if (subcategory.type === 'flares') {
                                return (
                                    <div key={`flares-${index}`}>
                                        {specialEventsData.activeCount > 1 && <div className={styles.singleResourceTitle}>{subcategory.title}</div>}
                                        <div className={styles.gemsPackages}>
                                            {subcategory.data.map((flare, idx) => (
                                                <Flares {...flare} key={idx} selectOfferById={selectOfferById} currency={currency} amount={flare.Amount} config={config} />
                                            ))}
                                        </div>
                                    </div>
                                );
                            }
                            if (subcategory.type === 'fortune') {
                                return (
                                    <div key={`fortune-${index}`}>
                                        {specialEventsData.activeCount > 1 && <div className={styles.singleResourceTitle}>{subcategory.title}</div>}
                                        <div className={styles.gemsPackages}>
                                            {subcategory.data.map((specialEvent, idx) => (
                                                <FortuneEventTokens {...specialEvent} key={idx} selectOfferById={selectOfferById} currency={currency} amount={specialEvent.Amount} config={config} />
                                            ))}
                                        </div>
                                    </div>
                                );
                            }
                            if (subcategory.type === 'gambit') {
                                return (
                                    <div key={`gambit-${index}`}>
                                        {specialEventsData.activeCount > 1 && <div className={styles.singleResourceTitle}>{subcategory.title}</div>}
                                        <div className={styles.offersWrapper}>
                                            {windowSize.isMobileView ?
                                                renderGambitOffersInCarousel(subcategory.title, subcategory.data, selectOfferById, currency, config, CI_SPECIAL_EVENTS, OFFER_NAME_TO_CONFIG_TYPE.gambitLeaguePackages)
                                                :
                                                renderGambitOffersNormal(subcategory.title, subcategory.data, selectOfferById, currency, config, CI_SPECIAL_EVENTS, OFFER_NAME_TO_CONFIG_TYPE.gambitLeaguePackages)
                                            }
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                </CSSTransition>
                {/* Web Shop Exclusive Offers Section */}
                <CSSTransition
                    in={webShopExclusiveOffers && webShopExclusiveOffers.length > 0 && offersCategories.webShopExclusiveOffers && offersCategories.webShopExclusiveOffers.isVisible}
                    timeout={400}
                    classNames="fade"
                    unmountOnExit
                >
                    {windowSize.isMobileView ?
                        renderOffersInCarousel(config.webShopExclusiveOffers, webShopExclusiveOffers, selectOfferById, currency, false, config.offerEnds, CI_EXCLUSIVE_OFFERS, OFFER_NAME_TO_CONFIG_TYPE.webShopExclusiveOffers, false)
                        :
                        renderOffersNormal(config.webShopExclusiveOffers, webShopExclusiveOffers, selectOfferById, currency, false,  config.offerEnds, CI_EXCLUSIVE_OFFERS, OFFER_NAME_TO_CONFIG_TYPE.webShopExclusiveOffers, false)
                    }
                </CSSTransition>
                {/* Event Pass Offers Section */}
                <CSSTransition
                    in={eventPassOffers && eventPassOffers.length > 0 && offersCategories.eventPassOffers && offersCategories.eventPassOffers.isVisible}
                    timeout={400}
                    classNames="fade"
                    unmountOnExit
                >
                    {windowSize.isMobileView ?
                        renderOffersInCarousel(config.eventPassOffers, eventPassOffers, selectOfferById, currency, true, config.offerEnds, eventPassIcon, OFFER_NAME_TO_CONFIG_TYPE.eventPassOffers, true)
                        :
                        renderOffersNormal(config.eventPassOffers, eventPassOffers, selectOfferById, currency, true,  config.offerEnds, eventPassIcon, OFFER_NAME_TO_CONFIG_TYPE.eventPassOffers, true)
                    }
                </CSSTransition>
                {/* Special Offers Section */}
                <CSSTransition
                    in={specialOffers && specialOffers.length > 0 && offersCategories.specialOffers && offersCategories.specialOffers.isVisible}
                    timeout={400}
                    classNames="fade"
                    unmountOnExit
                >
                    {windowSize.isMobileView ?
                        renderOffersInCarousel(config.specialOffers, specialOffers, selectOfferById, currency, false, config.offerEnds, CI_SPECIAL_OFFERS, OFFER_NAME_TO_CONFIG_TYPE.specialOffers)
                        :
                        renderOffersNormal(config.specialOffers, specialOffers, selectOfferById, currency, false,  config.offerEnds, CI_SPECIAL_OFFERS, OFFER_NAME_TO_CONFIG_TYPE.specialOffers)
                    }
                </CSSTransition>
                {/* Daily Offers Section */}
                <CSSTransition
                    in={everyDayOffers && everyDayOffers.length > 0 && offersCategories.everyDayOffers && offersCategories.everyDayOffers.isVisible}
                    timeout={400}
                    classNames="fade"
                    unmountOnExit
                >
                    {windowSize.isMobileView ?
                        renderOffersInCarousel(config.everyDayOffers, everyDayOffers, selectOfferById, currency, true, config.offerEnds, CI_DAILY_OFFERS, OFFER_NAME_TO_CONFIG_TYPE.everyDayOffers)
                        :
                        renderOffersNormal(config.everyDayOffers, everyDayOffers, selectOfferById, currency, true, config.offerEnds, CI_DAILY_OFFERS, OFFER_NAME_TO_CONFIG_TYPE.everyDayOffers)
                    }
                </CSSTransition>
                {/* Gems Section */}
                <CSSTransition
                    in={gems && gems.length > 0 && offersCategories.gems && offersCategories.gems.isVisible}
                    timeout={400}
                    classNames="fade"
                    unmountOnExit
                >
                    <div id={OFFER_NAME_TO_CONFIG_TYPE.gems} className={styles.gemsWrapper}>
                        <div className={styles.gemsTitle}>
                            <img className={styles.categoryIcon} src={CI_GEMS} alt={''} />
                            {config.gems}
                        </div>
                        <div className={styles.gemsPackages}>
                            {gems.map((gemPackage, index) => (
                                <GemsPackage {...gemPackage} key={index} selectOfferById={selectOfferById} currency={currency} gemsPackageNames={config.gemsPackages} />
                            ))}
                        </div>
                    </div>
                </CSSTransition>
                {/* Crates Section */}
                <CSSTransition
                    in={crates && crates.length > 0 && offersCategories.crates && offersCategories.crates.isVisible}
                    timeout={400}
                    classNames="fade"
                    unmountOnExit
                >
                    <div id={OFFER_NAME_TO_CONFIG_TYPE.crates} className={styles.cratesWrapper}>
                        <div className={styles.cratesTitle}>
                            <img className={styles.categoryIcon} src={CI_CRATES} alt={''} />
                            {config.crates}
                        </div>
                        <div className={styles.cratesPackages}>
                            {crates.map((cratePackage, index) => (
                                <CratesPackage
                                    {...cratePackage}
                                    key={index}
                                    selectOfferById={selectOfferById}
                                    currency={currency}
                                    ImageId={index}
                                    openCrateInfoPopUp={openCrateInfoPopUp}
                                    config={config}
                                />
                            ))}
                        </div>
                    </div>
                </CSSTransition>
                {/* Resources Section */}
                <CSSTransition
                    in={resources && Object.keys(resources).length > 0 && offersCategories.resources && offersCategories.resources.isVisible}
                    timeout={400}
                    classNames="fade"
                    unmountOnExit
                >
                    <div id={OFFER_NAME_TO_CONFIG_TYPE.resources} className={styles.resourcesWrapper}>
                        <div className={styles.resourcesTitle}>
                            <img className={styles.categoryIcon} src={CI_RESOURCES} alt={''} />
                            {config.resources}
                        </div>
                        {/* Group resources by type and render each group */}
                        {Object.entries(resources).map(([key, value]) => {
                            return (
                                <div key={key}>
                                    <div className={styles.singleResourceTitle}>{config.resourcesNames[key.toLowerCase()]}</div>
                                    <div className={styles.resourcesPackages}>
                                    {value.map((resourcesPackage, index) => (
                                        <ResourcesPackage {...resourcesPackage} key={index} selectOfferById={selectOfferById} currency={currency} resourcesNames={config.resourcesNames} />
                                    ))}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </CSSTransition>
                {/* Commander Offers Section */}
                <CSSTransition
                    in={commanderOffers && Object.keys(commanderOffers).length > 0 && offersCategories.commanders && offersCategories.commanders.isVisible}
                    timeout={400}
                    classNames="fade"
                    unmountOnExit
                >
                    <div id={OFFER_NAME_TO_CONFIG_TYPE.commanders} className={styles.commandersWrapper}>
                        <div className={styles.commandersTitle}>
                            <img className={styles.categoryIcon} src={CI_COMMANDER_OFFERS} alt={''} />
                            {config.commanderOffers}
                        </div>
                        {/* Group commander offers by type and render each group */}
                        {Object.entries(commanderOffers).map(([key, value]) => {
                            return (
                                <div key={key}>
                                    <div className={styles.commanderPackTitle}>{config.commanderResources[key.toLowerCase()]}</div>
                                    <div className={styles.commanderPackages}>
                                        {value.map((commanderPackage, index) => (
                                            <CommandersPackages
                                                {...commanderPackage}
                                                key={index}
                                                selectOfferById={selectOfferById}
                                                currency={currency}
                                                openCrateInfoPopUp={openCrateInfoPopUp}
                                                config={config}
                                                index={index}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </CSSTransition>
                {/* Mobile filter component */}
                {windowSize.isMobileView &&
                    <FilterMobile
                        offersCategories={categoriesOffers}
                        filterCategory={filterCategory}
                        text={config.allCategories}
                        textApply={config.filterClose}
                        categoryNames={{
                            eventPassOffers: config.eventPassOffers,
                            specialOffers: config.specialOffers,
                            everyDayOffers: config.everyDayOffers,
                            gems: config.gems,
                            crates: config.crates,
                            resources: config.resources,
                            webShopExclusiveOffers: config.webShopExclusiveOffers,
                            commanders: config.commanderOffers,
                            specialEvents: specialEventsData.title
                        }}
                    />
                }
            </div>
        </div>
    );
};

export default ShopOffers;